// src/pages/BrandManagement/components/LogoUploader.tsx
import React, { useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('brand_management');

interface LogoUploaderProps {
  currentLogoUrl: string | null | undefined;
  onUpload: (file: File) => void;
  onDelete: () => void;
  uploading: boolean;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  currentLogoUrl,
  onUpload,
  onDelete,
  uploading,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const location = useLocation();
  
  // Validate file
  const validateFile = (file: File): boolean => {
    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error(st('chi_chap_nhan_file_png_jpg_message'));
      return false;
    }
    
    // Check file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error(st('kich_thuoc_file_khong_duoc_vuot_message'));
      return false;
    }
    
    return true;
  };
  
  // Xử lý khi chọn file
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      onUpload(file);
    }
    // Reset input để có thể chọn lại cùng file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Xử lý drag & drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      const file = e.dataTransfer.files?.[0];
      if (file && validateFile(file)) {
        onUpload(file);
      }
    },
    [onUpload]
  );
  
  // Xử lý click vào upload area
  const handleClick = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <div>
      {/* Upload Area */}
      <div
        className={`logo-upload-area ${
          dragActive ? 'drag-active' : ''
        } ${
          uploading ? 'uploading' : ''
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{
          border: '2px dashed #ced4da',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          backgroundColor: dragActive ? '#f1f5ff' : '#f8f9fa',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s',
          borderColor: dragActive ? '#405189' : '#ced4da',
        }}
      >
        {currentLogoUrl ? (
          <img
            src={currentLogoUrl}
            alt={st('company_logo_alt')}
            style={{
              maxWidth: '200px',
              maxHeight: '80px',
              marginBottom: '16px',
              display: 'block',
              margin: '0 auto 16px',
            }}
          />
        ) : null}
        
        <div>
          <i
            className="ri-upload-cloud-2-line"
            style={{ fontSize: '32px', color: '#878a99' }}
          ></i>
          <p className="text-muted mb-1 mt-2">
            {st('keo_tha_file_hoac_text')}{' '}
            <span style={{ color: '#405189', cursor: 'pointer' }}>
              {st('chon_file_text')}
            </span>
          </p>
          <small className="text-muted">
            {st('png_jpg_svg_max_2mb_khuyen_hint')}
          </small>
        </div>
        
        {uploading && (
          <div className="mt-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">{st('dang_upload_text')}</span>
            </div>
            <p className="text-muted small mt-2 mb-0">{st('dang_upload_text')}</p>
          </div>
        )}
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        data-action-code="input-logo-upload"
        data-action-name="Upload logo"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={uploading || !PageActionPermission.canEnable(location.pathname, "input-logo-upload")}
      />

      {/* Delete button */}
      {currentLogoUrl && PageActionPermission.canDisplay(location.pathname, "button-delete-logo") && (
        <Button
          color="danger"
          outline
          size="sm"
          className="mt-2"
          data-action-code="button-delete-logo"
          data-action-name="Xóa logo"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(st('ban_co_chac_chan_muon_xoa_text'))) {
              onDelete();
            }
          }}
          disabled={uploading}
        >
          <i className="ri-delete-bin-line me-1"></i>
          {st('xoa_logo_button')}
        </Button>
      )}
    </div>
  );
};

export default LogoUploader;